import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { playSuccessSound, playBossSound } from '@/utils/sounds';
import { triggerButtonHaptic } from '@/utils/haptics';
import { GachiReward } from '@/components/GachiReward';

const confettiEmojis = ['🍟', '🤖', '🧖‍♂️', '🚲', '♂️', '💪', '👑'];

interface DeploymentBlockProps {
  onClose?: () => void;
}

export function DeploymentBlock({ onClose }: DeploymentBlockProps) {
  const [showConfetti, setShowConfetti] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [showGachiWarning, setShowGachiWarning] = useState(false);
  const [showGachiReward, setShowGachiReward] = useState(false);
  const [confettiParticles, setConfettiParticles] = useState<Array<{ id: string; emoji: string; x: number; y: number }>>([]);

  const handleExecute = () => {
    // 50% chance to show Gachi warning first
    if (!showGachiWarning && Math.random() > 0.5) {
      playBossSound();
      setShowGachiWarning(true);
      return;
    }

    playSuccessSound();
    triggerButtonHaptic();
    setShowConfetti(true);
    setShowGachiWarning(false);

    // Generate fewer, smaller confetti particles
    const particles = Array.from({ length: 15 }, (_, i) => ({
      id: `particle-${i}`,
      emoji: confettiEmojis[Math.floor(Math.random() * confettiEmojis.length)],
      x: Math.random() * 100,
      y: Math.random() * 100,
    }));
    setConfettiParticles(particles);

    setTimeout(() => {
      setShowMessage(true);
      // Show GachiReward after message appears
      setTimeout(() => {
        setShowGachiReward(true);
      }, 1500);
    }, 1500);
  };

  return (
    <>
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className={`relative w-full max-w-2xl border rounded-sm p-4 sm:p-8 transition-colors duration-500 ${
            showGachiWarning ? 'bg-indigo-900/40 border-indigo-500' : 'bg-[#191919] border-[#2F2F2F]'
          }`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="deployment-title"
        >
          {/* Confetti overlay - smaller and fewer */}
          <AnimatePresence>
            {showConfetti && (
              <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
                {confettiParticles.map((particle) => (
                  <motion.div
                    key={particle.id}
                    initial={{
                      x: '50%',
                      y: '50%',
                      scale: 0,
                      rotate: 0,
                    }}
                    animate={{
                      x: `${particle.x}%`,
                      y: `${particle.y}%`,
                      scale: [0, 0.8, 0.6],
                      rotate: [0, 360],
                      opacity: [1, 1, 0],
                    }}
                    transition={{
                      duration: 2,
                      ease: 'easeOut',
                    }}
                    className="absolute text-xl"
                    style={{
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                    }}
                  >
                    {particle.emoji}
                  </motion.div>
                ))}
              </div>
            )}
          </AnimatePresence>

          <div className="relative z-10">
            <h2 id="deployment-title" className="text-2xl sm:text-3xl font-bold text-white/90 mb-6">
              {showGachiWarning ? '[CRITICAL] Boss of This Gym Detected' : 'Final Deployment'}
            </h2>

            {showGachiWarning && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-indigo-500/20 border border-indigo-500/50 rounded-sm"
              >
                <p className="text-indigo-200 font-mono text-sm">
                  SYSTEM ERROR: High concentration of маскулинность detected. 
                  Local cluster is now under control of the Dungeon Master.
                  A fee of 300 bucks is required to proceed.
                </p>
              </motion.div>
            )}

            <div className="space-y-4 mb-8">
              <div className="p-4 bg-[#202020] border border-[#2F2F2F] rounded-sm">
                <div className="font-mono text-sm space-y-2">
                  <div className="text-[#9B9A97]">
                    <span className="text-[#6A8759]">Accuracy:</span> {showGachiWarning ? '300%' : '99.9%'}
                  </div>
                  <div className="text-[#9B9A97]">
                    <span className="text-[#6A8759]">Overfitting:</span> {showGachiWarning ? 'BUFFED' : '0%'}
                  </div>
                  <div className="text-[#9B9A97]">
                    <span className="text-[#6A8759]">Happiness:</span>{' '}
                    <span className="text-[#BBB529]">{showGachiWarning ? '♂️ DEEP DARK FANTASIES ♂️' : 'NaN (Optimizing...)'}</span>
                  </div>
                </div>
              </div>
            </div>

            {!showMessage ? (
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleExecute}
                className={`w-full py-3 sm:py-4 px-4 sm:px-6 font-bold rounded-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#191919] min-h-[44px] text-sm sm:text-base ${
                  showGachiWarning 
                    ? 'bg-indigo-600 hover:bg-indigo-700 text-white focus:ring-indigo-500' 
                    : 'bg-[#6A8759] hover:bg-[#7A9769] text-white focus:ring-[#6A8759]'
                }`}
                aria-label="Execute birthday wish and show confetti"
              >
                {showGachiWarning ? 'PAY 300 BUCKS & DEPLOY' : 'Execute Birthday Wish'}
              </motion.button>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center space-y-4"
              >
                <h3 className="text-2xl font-bold text-white/90">С Днём Рождения, Вова!</h3>
                <p className="text-[#9B9A97] text-lg">
                  Пусть 22-я эпоха будет самой продуктивной и счастливой!
                </p>
                <p className="text-[#9B9A97]">
                  Желаю успехов в ШАДе и всех твоих проектах!
                </p>
                <p className="text-[10px] text-white/20 uppercase tracking-widest mt-4">
                  You are now the boss of this site.
                </p>
              </motion.div>
            )}
          </div>
        </motion.div>
      </motion.section>
      
      {/* Gachi Reward overlay */}
      <GachiReward 
        visible={showGachiReward} 
        onDismiss={() => setShowGachiReward(false)} 
      />
    </>
  );
}
